/**
 * --------------------------------------------------------------------------
 * Bootstrap (v5.1.3): base-component.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
 * --------------------------------------------------------------------------
 */

import $ from 'jquery';
import Data from '../../bootstrap/dom/data.js';
import EventHandler from '../../bootstrap/dom/event-handler.js';
import SelectorEngine from '../../bootstrap/dom/selector-engine.js';
import { getElementFromSelector } from '../../bootstrap/util/index.js';

import Deferred from '../../util/deferred-promise.js';
import { parseDataAction } from '../../util/data.js';

import { animController } from '../../util/animate-css.js';
import { dropDuplicateResources } from '../../remote/partial-content.js';
import { getElement, isElementDisabled, isElementDisplayed } from '../../util/dom.js';
import { getOptionsFromDataset } from '../../util/dataset.js';
import { typeCheckConfig } from '../../util/plugin.js';
// import emitter from '../emitter';
// import { applyMixins } from '../../util/generic';

/**
 * Init component emitter
 * @mixin
 * @alias componentEmitter
 */
// const componentEmitter = emitter();

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const VERSION = '5.1.3';

/**
 * @typedef {{
 *  rootElement?: string;
 *  content?: string|Node|JQuery;
 *  contentSelector?: string;
 *  closable?: boolean;
 *  dismissible?: boolean;
 *  dismissActions?: MpDataAction[];
 *  disposeOnHide?: boolean;
 *  animated?: boolean;
 *  queueOnTransition?: boolean;
 *  [key: string]: any;
 * }} Config
 */

// TODO: extend a new component from this: MpDismissibleComponent extends MpBaseComponent

/**
 * BaseComponent
 *
 * @class
 * @abstract
 * // ----@mixes Emitter
 */
export default class MpBaseComponent {

	// ==============
	// Props
	// ==============

	/** @type {HTMLElement} */
	_element;

	/** @type {Config} */
	_config;

	/** @type {boolean} */
	_canDetachElement;

	/** @type {any[]} */
	_initArgs;

	/** @type {boolean} */
	_isDestroyed;

	/** @type {import('../../util/deferred-promise').Deferred} */
	_ac = null;

	/** @type {boolean} */
	_isShown = false;

	// =======================================================================================

	// ==============
	// Static Getters
	// ==============

	static get VERSION() {
		return VERSION;
	}

	/**
	 * @abstract
	 * @returns {string}
	 */
	static get NAME() {
		throw new Error('You have to implement the static getter "NAME", for each component!');
	}

	static get DATA_KEY() {
		return `bs.${this.NAME}`;
	}

	static get EVENT_KEY() {
		return `.${this.DATA_KEY}`;
	}

	static get SELECTOR() {
		return `div.${this.NAME}`;
	}

	/**
	 * @abstract
	 *
	 * @returns {{ [key: keyof Config]: string; }}
	 */
	static get DefaultType() {
		throw new Error('You have to implement the static getter "DefaultType", for each component!');
	}

	/**
	 * @abstract
	 * @returns {Config}
	 */
	static get Default() {
		throw new Error('You have to implement the static getter "Default", for each component!');
	}

	/**
	 * @abstract
	 * @returns {{ [key: string]: string|boolean; }=}
	 */
	static get DeprecatedConfig() {
		return {
			closable: 'dismissible',
		};
	}

	// ==============
	// Static Methods
	// ==============

	/**
	 * @abstract
	 * @param {Config} options
	 * @return {JQuery}
	 */
	static buildTemplate(options) {
		return $();
	}

	/**
	 * @param {JQuery|HTMLElement|string} element
	 * @returns {InstanceType<MpBaseComponent>}
	 */
	static getInstance(element) {
		return Data.get(getElement(element), this.DATA_KEY);
	}

	/**
	 * @param {HTMLElement} element
	 * @param {Config=} config
	 */
	static getOrCreateInstance(element, config) {
		try {
			return (element && this.getInstance(element)) || new this(element, config);
		}
		catch {
			// Ignore
			return null;
		}
	}

	/**
	 * Create a filter proxy object for the provided instance
	 *
	 * @param {MpBaseComponent} instance
	 * @param {{ [key: string]: any; }} filter
	 */
	static _getInstanceFilter(instance, filter) {
		return new Proxy({}, {
			get: (target, filterKey) => {
				if (typeof filterKey === 'symbol') {
					return undefined;
				}
				switch (filterKey) {
					case '$and': {
						// The return value must be the filter object itself, because the check is done against the filter object
						return this._checkInstanceFilter(instance, filter[filterKey], 'and') ? filter[filterKey] : null;
					}
					case '$or': {
						// The return value must be the filter object itself, because the check is done against the filter object
						return this._checkInstanceFilter(instance, filter[filterKey], 'or') ? filter[filterKey] : null;
					}
					case '$not': {
						// The return value must be the filter object itself, because the check is done against the filter object
						return this._checkInstanceFilter(instance, filter[filterKey], 'not') ? filter[filterKey] : null;
					}
					case 'self': {
						return instance;
					}
					case 'visible': {
						return isElementDisplayed(instance.getElement());
					}
					case 'preloading': {
						return instance.isPreloading();
					}
					case 'preloaded': {
						return instance.isPreloaded();
					}
					default: {
						return instance.getOptions()[filterKey];
					}
				}
			},
		});
	}

	/**
	 * Run the instance through filter checks using the provided filter object and the operator
	 *
	 * @param {MpBaseComponent} instance
	 * @param {{ [key: string]: any; }} filter
	 * @param { 'and' | 'or' | 'not' } operator
	 *
	 * @returns {boolean}
	 */
	static _checkInstanceFilter(instance, filter, operator = 'and') {

		// Operator logic:
		// - 'and': all filters must match
		// - 'or': at least one filter must match
		// - 'not': all filters must NOT match
		let atLeastOneMatch = false;
		const instanceFilterProxy = this._getInstanceFilter(instance, filter);

		for (const [filterKey, filterVar] of Object.entries(filter)) {

			// Exclude null filters
			if (filterVar == null) {
				continue;
			}

			// Filter mismatch
			if (instanceFilterProxy[filterKey] !== filterVar) {

				// One entry mismatch is enough for 'and' operator
				if (operator === 'and') {
					return false;
				}
			}
			else {
				atLeastOneMatch = true;

				// One entry match is enough for 'not' operator
				if (operator === 'not') {
					return false;
				}
			}
		}

		// 'or' operator requires at least one match
		if (operator === 'or') {
			return atLeastOneMatch;
		}

		// All filters matched
		return true;
	}

	/**
	 * @param {Config=} filter
	 * @returns {InstanceType<MpBaseComponent>[]}
	 */
	static getAll(filter = {}) {

		/** @type {HTMLElement[]} */
		const elements = SelectorEngine.find(this.SELECTOR);
		const instances = [];

		for (const element of elements) {
			const instance = this.getInstance(element);

			if (!instance) {
				console.debug('[%s] component instance not available yet for element: %o (using filter: %o)', this.NAME, element, filter);
				continue;
			}

			if (this._checkInstanceFilter(instance, filter, 'and')) {
				instances.push(instance);
			}
		}

		return instances;
	}

	/**
	 * Return the top most displayed instance
	 */
	static getLast() {
		return this.getInstance(SelectorEngine.find(this.SELECTOR).pop());
	}

	/**
	 * Return the top most displayed instance
	 */
	static getActive() {
		return this.getInstance(SelectorEngine.find(this.SELECTOR).filter(elem => isElementDisplayed(elem)).pop());
	}

	/**
	 * @param {MpBaseComponent=} instance
	 * @param {boolean=} withTransition
	 */
	static close(instance, withTransition = true) {
		if (!instance) {
			instance = this.getActive();
		}

		if (instance) {
			instance.close(withTransition);
		}
	}

	/**
	 * @param {Config=} filter
	 * @param {boolean=} withTransition
	 */
	static closeAll(filter, withTransition = false) {

		const instances = this.getAll(filter);

		for (const instance of instances) {
			try {
				// Close the instance
				instance.close(withTransition);
			}
			catch (error) {
				console.warn('Error while closing %o: %o', this.NAME, error);
			}
		}
	}

	/**
	 * @param {string} method
	 * @param {boolean} requireAvailableInstance This will only enable the trigger if an instance is available - there was a problem when the getOrCreateInstance always return an instance (with no _element); now the constructor will throw an error if no element is found
	 */
	static enableDismissTrigger(method = 'hide', requireAvailableInstance = true) {

		// eslint-disable-next-line consistent-this
		const ctr = this;
		const clickEvent = `click.dismiss${ctr.EVENT_KEY}`;
		const name = ctr.NAME;

		// Attach click event to document (use the selector for 'contains' to allow multiple dismiss types)
		EventHandler.on(document, clickEvent, `[data-bs-dismiss*="${name}"]`, /** @this {HTMLElement} */ function(event) {

			if (isElementDisabled(this)) {
				return;
			}

			/** @type {HTMLElement} */
			let target = getElementFromSelector(this);
			if (!target || !target.matches(`.${name}`)) {
				target = this.closest(`.${name}`);
			}
			const instance = (requireAvailableInstance === true) ? ctr.getInstance(target) : ctr.getOrCreateInstance(target);

			// Instance found
			if (instance) {

				// Cancel default handler
				// ? Note: find out why this was needed, usually the buttony either has a location action or a dismiss action, not both
				// ? Note2: in shop templates there are a lot of places where the button has both a location and a dismiss action
				// if (['A', 'AREA'].includes(this.tagName) || Object.hasOwn(this.dataset, 'href') || Object.hasOwn(this.dataset, 'url')) {
				// 	event.preventDefault();
				// 	event.stopPropagation();
				// 	event.stopImmediatePropagation();
				// }

				// Cancel only the default browser handler
				if (['A', 'AREA'].includes(this.tagName)) {
					event.preventDefault();
				}

				// Method argument is left, for Alert and only, as it doesn't implement the 'hide' method
				instance[method]();
			}
		});
	}

	/**
	 * @param {typeof MpBaseComponent} Component
	 */
	static jQueryInterface(Component) {

		/**
		 * @this {JQuery}
		 * @param {*} methodNameOrConfig
		 * @param {HTMLElement=} relatedTarget
		 */
		return function(methodNameOrConfig, relatedTarget) {

			return this.each(function() {

				const instance = Component.getOrCreateInstance(this, methodNameOrConfig);

				if (typeof methodNameOrConfig === 'string') {
					if (instance[methodNameOrConfig] === undefined || methodNameOrConfig.startsWith('_') || methodNameOrConfig === 'constructor') {
						throw new TypeError(`No method named "${methodNameOrConfig}"`);
					}

					instance[methodNameOrConfig](relatedTarget || this);
				}
			});
		};
	}

	static defineJQueryPlugin() {
		$(() => {
			// eslint-disable-next-line consistent-this
			const plugin = this;
			const name = plugin.NAME;
			const JQUERY_NO_CONFLICT = $.fn[name];
			const Builder = plugin.jQueryInterface(plugin);
			// @ts-ignore
			Builder.Constructor = plugin;
			// @ts-ignore
			Builder.noConflict = () => {
				$.fn[name] = JQUERY_NO_CONFLICT;
				return Builder;
			};
			$.fn[name] = Builder;
		});
	}

	// ==============
	// Public Methods
	// ==============

	/**
	 * @param {string|HTMLElement|Config} elementOrConfig
	 * @param {Config=} config
	 */
	constructor(elementOrConfig, config) {

		// Save the init args for initialization
		this._initArgs = [elementOrConfig, config];

		// Initialize the component
		this.init(elementOrConfig, config);
	}

	/**
	 * Use this method instead of the constructor to be able to re-construct the instance after it gets destroyed
	 *
	 * @param {string|HTMLElement|Config} elementOrOptions
	 * @param {Config=} options
	 */
	init(elementOrOptions, options) {

		// Flag as not destroyed
		this._isDestroyed = false;
		this._canDetachElement = true;

		let config;
		/** @type {any} */
		const ctr = this.constructor;

		// Nothing provided in the constructor
		if (elementOrOptions == null && options == null) {
			throw new Error(`Component ${ctr.NAME} could not be initialized from empty arguments`);
		}

		// Try to get the element from the first argument
		this._element = getElement(elementOrOptions);

		// Provided a config object
		if (this._element === null) {

			// Save extended config
			config = {
				// @ts-ignore
				...ctr.Default,
				// @ts-ignore
				...(elementOrOptions),
			};
			// Init from html markup
			this._element = ctr.buildTemplate(config)[0];

			if (!this._element) {
				throw new Error(`Component ${ctr.NAME} could not be initialized from config object`);
			}
		}
		else {
			config = options || {};
		}

		// Add instance to map
		Data.set(this._element, ctr.DATA_KEY, this);

		// Set the config
		this._config = this._getConfig(config);
	}

	/**
	 * Re-initialize the instance
	 */
	reInit() {
		if (this._isDestroyed) {
			// @ts-ignore
			// eslint-disable-next-line prefer-spread
			this.init.apply(this, this._initArgs);
		}
		else {
			console.warn('Cannot re-initialize an already initialized component (%o)', this);
		}

		return this;
	}

	getOptions() {
		return this._config;
	}

	getElement() {
		return this._element;
	}

	getAnim() {
		if (this._ac) {
			return this._ac;
		}
		return Deferred.resolve(this._element);
	}

	/**
	 * @param {HTMLElement} element
	 * @param {string|string[]} classList
	 * @param {'add_remove'|'remove_add'|'add'|'remove'|'none'} mode
	 * @param {boolean} waitForEnd
	 */
	startAnim(element, classList, mode = 'add_remove', waitForEnd = true) {
		this._ac = animController({
			element,
			classList,
			mode,
			waitForEnd,
		}).always((_) => {
			this._ac = null;
		});
		return this._ac;
	}

	endAnim() {
		if (this._ac) {
			this._ac.resolve(this._element);
		}

		return this;
	}

	isPreloading() {
		return false;
	}

	isPreloaded() {
		return true;
	}

	isVisible() {
		return this._isShown;
	}

	/**
	 * Close & dispose the instance
	 *
	 * @param {boolean} forceNoTransition
	 * @abstract
	 */
	close(forceNoTransition = true) {
		// this.off('test');
		throw new Error('You have to implement the "close" method, for each component!');
	}

	dispose() {

		// End current transition
		this.endAnim();

		// Already disposed
		// ! Note: This MUST be called after endAnim() method because if will also call dispose() and it might skip the _isDestroyed check
		if (this._isDestroyed) {
			return;
		}

		/** @type {any} */
		const ctr = this.constructor;
		const element = this._element;

		// Remove data
		Data.remove(element, ctr.DATA_KEY);

		// Remove all events
		EventHandler.off(element, ctr.EVENT_KEY);

		// Remove the element from dom (use JQuery to remove additional events + data)
		if (this._canDetachElement) {
			$(element).remove();
		}

		// Clean class props
		for (const propertyName of Object.getOwnPropertyNames(this)) {
			if (propertyName !== '_initArgs' && propertyName !== '_instanceId') {
				this[propertyName] = null;
			}
		}

		this._isDestroyed = true;
	}

	// ==========================
	// Private/Protected Methods
	// ==========================

	/**
	 * @protected
	 *
	 * @param {Config} config
	 *
	 * @returns {Config}
	 */
	_normalizeConfig(config) {
		return config;
	}

	/**
	 * @protected
	 *
	 * @param {Config} config
	 *
	 * @returns {Config}
	 */
	_getConfig(config = {}) {

		/** @type {any} */
		const ctr = this.constructor;

		config = {
			...ctr.Default,
			...getOptionsFromDataset(this._element, 'bs'),
			...config,
		};

		// Check validity of the url
		if (config.url) {
			const url = (config.url instanceof URL) ? config.url.href : config.url;
			if (/^(#|javascript:|mailto:|tel:)/i.test(url)) {
				console.warn('Invalid url provided: %o', url);
				config.url = null;
			}
		}

		typeCheckConfig(ctr.NAME, config, ctr.DefaultType, ctr.DeprecatedConfig);

		return config;
	}

	/**
	 * @protected
	 */
	_attachElement() {

		const domElement = this._element;

		// Add element to DOM
		if (!domElement.parentNode || domElement.parentNode.nodeType !== Node.ELEMENT_NODE) {

			// Make sure the modal is not displayed
			domElement.style.display = 'none';
			// Don't move modal's DOM position
			// document.body.append(domElement);
			const rootElement = getElement(this._config.rootElement) || document.body;

			// Use jQuery to attach the component, in order to execute script elements
			dropDuplicateResources($(domElement)).appendTo(rootElement);

			// Allow modal detach
			this._canDetachElement = true;
		}
		// Element already in DOM
		else {
			this._canDetachElement = false;
		}
	}

	/**
	 * @protected
	 *
	 * @param {MpDataAction[]} dismissActions
	 */
	_handleDismissActions(dismissActions) {

		for (const action of dismissActions) {

			// Get action name and arguments
			const [actionName, actionArgs] = parseDataAction(action);

			if (mpro.actionHandlers[actionName]) {
				mpro.actionHandlers[actionName](...actionArgs);
			}
			else {
				console.warn('Invalid dismiss action: %o', action);
			}
		}
	}
}

// Extend with the emitter interface
// Object.assign(MpBaseComponent.prototype, componentEmitter);
// // applyMixins(MpBaseComponent, [componentEmitter.constructor]);

// @ts-ignore
// window.MpBaseComponent = MpBaseComponent;